import React from 'react';
import { TrendingUp, DollarSign, Users, Target, Rocket, Building, Globe, Award, Calendar, Mail, Phone, FileText, BarChart3, PieChart } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const InvestorsPage: React.FC = () => {
  useSEO('investors' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  const keyHighlights = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Business Growth",
      subtitle: "Expanding Portfolio",
      description: "Growing AI services portfolio serving diverse industries and market segments"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Client Focus",
      subtitle: "Service Excellence",
      description: "Committed to delivering high-quality AI solutions and maintaining strong client relationships"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Market Presence",
      subtitle: "Nationwide Coverage",
      description: "Established presence serving clients across major US metropolitan areas"
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: "Diverse Clientele",
      subtitle: "Multiple Sectors",
      description: "Serving clients across healthcare, finance, retail, and technology industries"
    }
  ];

  const marketOpportunity = [
    {
      title: "AI Services Market",
      value: "$432B",
      timeframe: "by 2027",
      growth: "19.6% CAGR",
      description: "Global artificial intelligence market expanding rapidly across all sectors"
    },
    {
      title: "Cloud Infrastructure",
      value: "$1.2T",
      timeframe: "by 2030",
      growth: "15.7% CAGR",
      description: "Enterprise cloud adoption creating massive infrastructure opportunities"
    }
  ];

  const competitiveAdvantages = [
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Comprehensive AI Portfolio",
      description: "Specialized AI services covering the entire digital transformation journey from marketing to compliance"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Healthcare AI Solutions",
      description: "Specialized AI services for healthcare including medical data analytics, patient management automation, and HIPAA-compliant solutions"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Strategic Positioning",
      description: "Dual-coast presence in Michigan and Texas providing nationwide coverage and talent access"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Execution Focus",
      description: "Committed to successful AI implementations and building long-term client relationships"
    }
  ];

  const productRoadmap = [
    {
      quarter: "Q1 2025",
      milestone: "AI Services Expansion",
      description: "Launch enhanced Cognitive Generate and Vision capabilities",
      status: "In Development"
    },
    {
      quarter: "Q2 2025",
      milestone: "Strategic Partnerships",
      description: "Enterprise partnerships with major technology and healthcare systems",
      status: "Negotiations"
    },
    {
      quarter: "Q4 2025",
      milestone: "Service Expansion",
      description: "Launch advanced AI analytics and automation services for healthcare sector",
      status: "Planned"
    }
  ];

  const leadership = [
    {
      name: "Executive Leadership Team",
      description: "Experienced leaders with expertise in AI, healthcare technology, and enterprise software development"
    },
    {
      name: "Technical Advisory Board",
      description: "Industry experts guiding product development and market strategy"
    },
    {
      name: "Healthcare Advisory Panel",
      description: "Medical professionals ensuring regulatory compliance and clinical workflow optimization"
    }
  ];

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
            Business Overview
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
            AI-Powered Digital Transformation Solutions
          </p>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Learn about our comprehensive AI services portfolio and business development initiatives,
            including specialized healthcare AI solutions, automation services, and custom software development.
          </p>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Business Highlights</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Key areas of focus driving our business development and market presence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyHighlights.map((highlight, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4 flex justify-center">
                  {highlight.icon}
                </div>
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-3">{highlight.subtitle}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{highlight.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl p-8 border border-blue-200 dark:border-blue-500/30">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Market Opportunity</h2>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                Positioned at the intersection of multiple high-growth technology markets
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {marketOpportunity.map((market, index) => (
                <div key={index} className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">{market.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">{market.timeframe}</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{market.title}</div>
                  <div className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-3">{market.growth}</div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{market.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Competitive Advantages</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Strategic differentiators that position us for sustained market leadership
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {competitiveAdvantages.map((advantage, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-start space-x-4">
                  <div className="text-blue-600 dark:text-blue-400 mt-1">
                    {advantage.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{advantage.title}</h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{advantage.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Roadmap */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Product Roadmap & Milestones</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Strategic development timeline driving revenue growth and market expansion
            </p>
          </div>

          <div className="space-y-6">
            {productRoadmap.map((item, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="text-blue-600 dark:text-blue-400 font-semibold">{item.quarter}</div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.status === 'In Development' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                        item.status === 'Negotiations' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                      }`}>
                        {item.status}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{item.milestone}</h3>
                    <p className="text-gray-700 dark:text-gray-300">{item.description}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400 mt-4 md:mt-0 md:ml-6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Leadership & Governance</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Experienced leadership driving innovation and strategic growth
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {leadership.map((team, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{team.name}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{team.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Information */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Investment Highlights</h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Revenue Diversification</h3>
                <p className="text-gray-700 dark:text-gray-300">Multiple revenue streams across specialized AI service categories</p>
              </div>
              <div className="text-center">
                <PieChart className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Market Expansion</h3>
                <p className="text-gray-700 dark:text-gray-300">Healthcare AI and enterprise automation markets represent significant growth opportunities</p>
              </div>
              <div className="text-center">
                <DollarSign className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Scalable Model</h3>
                <p className="text-gray-700 dark:text-gray-300">High-margin AI services with recurring revenue potential</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-600/10 to-blue-700/10 rounded-xl p-6 text-center">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Business Development</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Exploring strategic partnerships and opportunities to expand our AI services and healthcare technology development
              </p>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                For business development information and partnership opportunities, please contact our team
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Business Development Contact
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              For partnership opportunities, business development inquiries, or general information
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="flex flex-col items-center">
                <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <div className="font-medium text-gray-900 dark:text-white">Email</div>
                <a href="mailto:team@cognitivecrafthub.com" className="text-blue-600 dark:text-blue-400 hover:underline">
                  team@cognitivecrafthub.com
                </a>
              </div>
              <div className="flex flex-col items-center">
                <Phone className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <div className="font-medium text-gray-900 dark:text-white">Phone</div>
                <a href="tel:+15172025360" className="text-blue-600 dark:text-blue-400 hover:underline">
                  +1 (517) 202-5360
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:team@cognitivecrafthub.com?subject=Partnership Inquiry"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </a>
              <a
                href="/contact"
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Important Legal Notice</h3>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              <p>
                <strong>No Investment Solicitation:</strong> This page is for informational purposes only and does not constitute 
                an offer to sell or a solicitation of an offer to buy any securities. No securities are being offered for sale 
                through this website or any communication herein.
              </p>
              <p>
                <strong>Forward-Looking Statements:</strong> This page contains forward-looking statements regarding our business, 
                products, and market opportunities. These statements involve risks and uncertainties. Actual results may differ 
                materially from those projected. Market data is based on third-party research and industry reports.
              </p>
              <p>
                <strong>Service Development:</strong> References to new services and features are development plans
                subject to change. Service features, specifications, and availability dates may be modified based on
                technical development, regulatory requirements, and market conditions.
              </p>
              <p>
                <strong>No Financial Advice:</strong> Information contained herein does not constitute financial, investment, or 
                business advice. Interested parties should conduct their own due diligence and consult with qualified advisors 
                before making any business or investment decisions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};